/**
 * useLiveRecorder — host-side recording for a Live Class.
 *
 * Captures the entire tab (preferred) or the local camera+mic via
 * MediaRecorder, accumulates Blob chunks, and lets the host:
 *   - download the WebM directly to local drive
 *   - upload it to the platform via /api/school/.../recordings/upload
 *
 * Designed to run alongside the WebRTC mesh — it captures what the host
 * sees, so all participants who appear on screen are included.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const pickMime = () => {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm;codecs=h264,opus',
    'video/webm',
    'video/mp4',
  ];
  for (const t of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(t)) {
      return t;
    }
  }
  return '';
};

export default function useLiveRecorder({ tenantId, sessionId, autoUpload = false } = {}) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastBlob, setLastBlob] = useState(null);
  const chunksRef = useRef([]);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const tickerRef = useRef(null);
  const startedAtRef = useRef(0);
  const autoUploadRef = useRef(autoUpload);
  autoUploadRef.current = autoUpload;
  const uploadBlobRef = useRef(null);

  const stopAndCleanup = useCallback(() => {
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch {} });
      streamRef.current = null;
    }
  }, []);

  const start = useCallback(async () => {
    if (recording) return;
    chunksRef.current = [];
    setLastBlob(null);
    setSeconds(0);
    let stream;
    try {
      // Prefer full display capture (browser tab w/ system audio when available)
      stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser', frameRate: 30 },
        audio: true,
      });
      // Try to add the host mic on top so commentary is captured even if
      // the tab doesn't expose audio.
      try {
        const mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        const mixed = new MediaStream();
        stream.getVideoTracks().forEach((t) => mixed.addTrack(t));
        // Combine the screen audio + mic into a single track via WebAudio
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const dest = ctx.createMediaStreamDestination();
        const screenAudio = stream.getAudioTracks();
        if (screenAudio.length) {
          ctx.createMediaStreamSource(new MediaStream([screenAudio[0]])).connect(dest);
        }
        ctx.createMediaStreamSource(mic).connect(dest);
        dest.stream.getAudioTracks().forEach((t) => mixed.addTrack(t));
        // Stop original tracks once mixed (screen video already added above)
        if (screenAudio.length) screenAudio.forEach((t) => t.stop());
        stream = mixed;
      } catch {
        // Mic permission denied — keep raw display stream
      }
    } catch (err) {
      // Fall back to camera + mic when display capture is rejected
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      } catch (e) {
        throw new Error('Permission denied: cannot access camera/microphone or screen.');
      }
    }
    streamRef.current = stream;
    const mime = pickMime();
    const rec = mime ? new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_500_000 }) : new MediaRecorder(stream);
    recorderRef.current = rec;
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'video/webm' });
      setLastBlob(blob);
      setRecording(false);
      stopAndCleanup();
      // A lesson nobody remembered to send is a lesson the absent student
      // never gets: when the class ends the copy goes up by itself.
      if (autoUploadRef.current && uploadBlobRef.current) {
        const elapsed = Math.floor((Date.now() - startedAtRef.current) / 1000);
        uploadBlobRef.current(blob, elapsed).catch(() => {});
      }
    };
    // Stop the recording automatically if the user clicks "Stop sharing" in the browser UI
    stream.getVideoTracks().forEach((t) => { t.onended = () => { try { rec.stop(); } catch {} }; });
    startedAtRef.current = Date.now();
    rec.start(2000); // 2-second chunks for resilience
    setRecording(true);
    tickerRef.current = setInterval(() => {
      setSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
  }, [recording, stopAndCleanup]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      try { recorderRef.current.stop(); } catch {}
    }
  }, []);

  const downloadLast = useCallback((filename = `class-recording-${Date.now()}.webm`) => {
    if (!lastBlob) return;
    const url = URL.createObjectURL(lastBlob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  }, [lastBlob]);

  // A lecture is a big file and the connection in a lecture hall is not. It
  // goes up in pieces, each one retried on its own, so one dropped packet does
  // not cost the whole class.
  const uploadBlob = useCallback(async (blob, durationSeconds) => {
    if (!blob || !tenantId || !sessionId) return null;
    const base = `${API_URL}/api/school/tenants/${tenantId}/live-classes/${sessionId}/recordings`;
    const filename = `class-${sessionId}-${Date.now()}.webm`;
    setUploading(true);
    setUploadProgress(0);
    try {
      const init = await fetch(`${base}/upload/init`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ filename, total_bytes: blob.size }),
      }).then((r) => (r.ok ? r.json() : Promise.reject(new Error('Upload could not be started'))));

      const chunkSize = init.chunk_size_hint || 4 * 1024 * 1024;
      let index = init.next_index || 0;
      let offset = index * chunkSize;
      while (offset < blob.size) {
        const piece = blob.slice(offset, offset + chunkSize);
        let attempt = 0;
        // Retry the piece, not the lecture.
        for (;;) {
          try {
            const res = await fetch(`${base}/upload/${init.upload_id}/chunk?index=${index}`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/octet-stream', ...authHeaders() },
              body: piece,
            });
            if (!res.ok) throw new Error(`Chunk rejected (${res.status})`);
            const body = await res.json();
            if (body.accepted === false) {
              index = body.resume_from;
              offset = index * chunkSize;
            } else {
              index = body.next_index;
              offset += piece.size;
            }
            break;
          } catch (err) {
            attempt += 1;
            if (attempt >= 4) throw err;
            await new Promise((r) => setTimeout(r, 1000 * attempt));
          }
        }
        setUploadProgress(Math.min(99, Math.round((offset / blob.size) * 100)));
      }

      const done = await fetch(`${base}/upload/${init.upload_id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ duration_seconds: Math.max(0, Math.floor(durationSeconds || 0)) }),
      }).then((r) => (r.ok ? r.json() : Promise.reject(new Error('The recording could not be saved'))));
      setUploadProgress(100);
      return done;
    } finally {
      setUploading(false);
    }
  }, [tenantId, sessionId]);
  uploadBlobRef.current = uploadBlob;

  const uploadLast = useCallback(async () => {
    if (!lastBlob) return null;
    return uploadBlob(lastBlob, seconds);
  }, [lastBlob, seconds, uploadBlob]);

  // Cleanup on unmount
  useEffect(() => () => { stop(); stopAndCleanup(); }, [stop, stopAndCleanup]);

  return {
    recording, seconds, lastBlob,
    uploading, uploadProgress,
    start, stop, downloadLast, uploadLast, uploadBlob,
  };
}
